import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Layout, Button, Dropdown, Avatar, Drawer, message, Spin, Menu } from 'antd';
import { CalendarOutlined, UserOutlined, LinkOutlined, UnorderedListOutlined, LogoutOutlined, KeyOutlined, ProfileOutlined, MenuOutlined } from '@ant-design/icons';
// ThemeToggle removed
import TaskDashboard from './pages/TaskDashboard';
import UserManagement from './components/UserManagement';
import AvailableList from './components/AvailableList';
import UserReservationsList from './components/UserReservationsList';
import Login from './components/Login';
import ChangePassword from './components/ChangePassword';
import ApiKeyConfig from './components/ApiKeyConfig';
import { isAuthenticated, getUser, logout } from './utils/api';
import { initMobileOptimization } from './utils/mobile';
import './App.css';

const { Content, Header } = Layout;

// 路由保护组件
const ProtectedRoute = ({ children }) => {
  const isLoggedIn = isAuthenticated();
  
  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

export const AppContent = ({ user, onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [changePasswordVisible, setChangePasswordVisible] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  
  const menuItems = [
    {
      key: 'tasks',
      icon: <CalendarOutlined />,
      label: '预约',
    },
    {
      key: 'available',
      icon: <UnorderedListOutlined />,
      label: '空档',
    },
    {
      key: 'reservations',
      icon: <ProfileOutlined />,
      label: '记录',
    },
    {
      key: 'users',
      icon: <UserOutlined />,
      label: '用户',
    },
    {
      key: 'apikey',
      icon: <KeyOutlined />,
      label: 'API Key',
    },
  ];

  const getSelectedKey = () => {
    if (location.pathname.includes('/users')) return 'users';
    if (location.pathname.includes('/apikey')) return 'apikey';
    if (location.pathname.includes('/reservations')) return 'reservations';
    if (location.pathname.includes('/available')) return 'available';
    return 'tasks';
  };

  const handleMenuClick = ({ key }) => {
    setMobileNavOpen(false);
    if (key === 'tasks') {
      navigate('/tasks');
    } else if (key === 'available') {
      navigate('/available');
    } else if (key === 'reservations') {
      navigate('/reservations');
    } else if (key === 'users') {
      navigate('/users');
    } else if (key === 'apikey') {
      navigate('/apikey');
    }
  };

  const handleJumpToReserve = () => {
    window.open('https://general-master.joyreserve.com/pages/Home/index?system=11ef2a1b37e0bbc0', '_blank');
  };

  const handleLogout = async () => {
    try {
      await logout();
      message.success('注销成功');
      onLogout();
    } catch (error) {
      message.error('注销失败');
    }
  };

  const userMenuItems = [
    {
      key: 'changePassword',
      icon: <KeyOutlined />,
      label: '修改密码',
      onClick: () => setChangePasswordVisible(true),
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '注销',
      onClick: handleLogout,
    },
  ];

  return (
    <Layout className="layout">
      <Header className="mobile-responsive-header">
        <div className="header-left">
          <Button
            type="text"
            icon={<MenuOutlined />}
            className="mobile-nav-trigger"
            aria-label="打开导航菜单"
            onClick={() => setMobileNavOpen(true)}
          />
          <div className="header-brand">
            <div className="header-title-mobile">
              羽毛球场地预约系统
            </div>
            <div className="header-subtitle">
              工作台
            </div>
          </div>
        </div>
        <div className="header-nav">
          <Menu
            mode="horizontal"
            selectedKeys={[getSelectedKey()]}
            items={menuItems}
            onClick={handleMenuClick}
            style={{ borderBottom: 'none', background: 'transparent' }}
          />
        </div>
        <div className="header-right">
          <Button
            type="primary"
            icon={<LinkOutlined />}
            onClick={handleJumpToReserve}
            className="jump-button-mobile header-utility-button"
            size="small"
            style={{ marginRight: '16px' }}
          >
            <span className="jump-button-text">跳转到预约平台</span>
          </Button>
          <Dropdown
            menu={{ items: userMenuItems }}
            placement="bottomRight"
            trigger={['click']}
          >
            <div className="user-dropdown-mobile">
              <Avatar size="small" icon={<UserOutlined />} />
              <span className="username-mobile">{user?.username || 'Admin'}</span>
            </div>
          </Dropdown>
        </div>
      </Header>
      <Drawer
        title="导航"
        placement="left"
        open={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        className="mobile-nav-drawer"
      >
        <Menu
          mode="inline"
          selectedKeys={[getSelectedKey()]}
          items={menuItems}
          onClick={handleMenuClick}
        />
        <Button
          type="primary"
          icon={<LinkOutlined />}
          onClick={handleJumpToReserve}
          className="mobile-drawer-jump-button"
          block
        >
          跳转到预约平台
        </Button>
      </Drawer>
      <Content className="app-content">
        <div className="app-content-inner">
          <Routes>
            <Route path="/tasks" element={<ProtectedRoute><TaskDashboard /></ProtectedRoute>} />
            <Route path="/available" element={<ProtectedRoute><AvailableList /></ProtectedRoute>} />
            <Route path="/reservations" element={<ProtectedRoute><UserReservationsList /></ProtectedRoute>} />
            <Route path="/users" element={<ProtectedRoute><UserManagement /></ProtectedRoute>} />
            <Route path="/apikey" element={<ProtectedRoute><ApiKeyConfig /></ProtectedRoute>} />
            <Route path="/" element={<Navigate to="/tasks" />} />
          </Routes>
        </div>
      </Content>
      
      <ChangePassword 
        visible={changePasswordVisible} 
        onClose={() => setChangePasswordVisible(false)} 
      />
    </Layout>
  );
};

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 初始化移动端优化
    initMobileOptimization();
    
    // 检查登录状态
    const checkAuth = () => {
      if (isAuthenticated()) {
        const storedUser = getUser();
        setUser(storedUser || null);
        setIsLoggedIn(true);
      } else {
        setIsLoggedIn(false);
        setUser(null);
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const handleLogin = (userData, token) => {
    setUser(userData);
    setIsLoggedIn(true);
  };

  const handleLogout = () => {
    setUser(null);
    setIsLoggedIn(false);
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <Spin size="large" />
        <div style={{ color: 'var(--text-slate-700)', fontSize: '16px', fontWeight: '600' }}>正在初始化系统...</div>
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        <Route path="/login" element={!isLoggedIn ? <Login onLogin={handleLogin} /> : <Navigate to="/" />} />
        <Route path="/*" element={isLoggedIn ? <AppContent user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
      </Routes>
    </Router>
  );
}

export default App;
